import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BodyLargeRegular, Highlight, TitleLarge, TitleMedium } from "../../../components/font";
import TransitionWrapper from "../../../components/transition-wrapper/TransitionWrapper";
import Container from "../../../layout/container";
import ScrollableContainer from "../../../layout/scrollable-container";
import { UseExploreIRAOptionsType } from "../useExploreIRAOptions";
import { DraggableNumber, DraggableTile, DraggableTileContainer } from "./styles";
import { Button, OptionTile } from "@manifesthhqinc/ui-library";
import { TransferOptionValues } from "../../../utils/getTransferOptionContent";
import Footer from "../../../components/footer";
import { Feature, FINANCIAL_ADVISOR, GOAL_PLANNING } from "../../../class/iraQuestionnaire";
import HeadingContainer from "../../../layout/heading-container";

export const getOptionTitleById = (
  id: Feature,
  iraType: TransferOptionValues.ira | TransferOptionValues.managedIRA,
): string => {
  switch (id) {
    case GOAL_PLANNING:
      return "Goal planning tools";
    case FINANCIAL_ADVISOR:
      if (iraType === TransferOptionValues.ira) {
        return "Access to real-person support";
      }
      return "Human advice when needed";
    case "TECH":
      if (iraType === TransferOptionValues.ira) {
        return "Mobile or web experience";
      }
      return "A smooth, intuitive mobile app";
    case "ROBO":
      return "Automated investing (like rebalancing, tax optimization)";
    case "FEES":
      return "Low fees and no minimums";
    case "RESEARCH_TOOLS":
      return "Research & market insights";

    case "INVESTMENT_OPTIONS":
      return "Range of investments (stocks, ETFs, crypto)";
    default:
      return "";
  }
};
const SortableItem = (props: any) => {
  const { attributes, listeners, setNodeRef, transform, transition, index } = useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    flex: 1,
    display: "inline-flex",
  };

  return (
    <DraggableTile>
      <DraggableNumber>
        <TitleMedium>
          <Highlight>{index + 1}</Highlight>
        </TitleMedium>
      </DraggableNumber>
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <OptionTile variant="moveHandle" appearance="secondary">
          {getOptionTitleById(props.id as Feature, props.iraType)}
        </OptionTile>
      </div>
    </DraggableTile>
  );
};

const ExploreIRAOptionQuestionOne = (props: UseExploreIRAOptionsType) => {
  const { featurePriority, userSelectedTransferOption, setFeaturePriority, onClickContinue } = props;
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setFeaturePriority((i) => {
        const oldIndex = i.indexOf(active.id);
        const newIndex = i.indexOf(over.id);
        return arrayMove(i, oldIndex, newIndex);
      });
    }
  };
  return (
    <TransitionWrapper>
      <ScrollableContainer name="henry-questionnaire-form-1">
        <Container>
          <HeadingContainer paneVariant="optionForm" withProgressBar>
            <TitleLarge>What matters the most to you in an IRA account?</TitleLarge>
            <BodyLargeRegular>Drag and move the box to reorder the list by your priorities</BodyLargeRegular>
          </HeadingContainer>
          <DraggableTileContainer>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={featurePriority} strategy={verticalListSortingStrategy}>
                {featurePriority.map((id) => (
                  <SortableItem key={id} id={id} iraType={userSelectedTransferOption} />
                ))}
              </SortableContext>
            </DndContext>
          </DraggableTileContainer>
        </Container>
      </ScrollableContainer>
      <Footer>
        <Button name="continue" onClick={() => onClickContinue()} size="large">
          Continue
        </Button>
      </Footer>
    </TransitionWrapper>
  );
};

export default ExploreIRAOptionQuestionOne;
